/**
 * Tagged-value store for script variables.
 *
 * Missing keys return U (Unknown). Writes notify listeners keyed by
 * variable name. Port of blackboard.py.
 */

import { Ternary, type TernaryValue } from "./Ternary";

/** Tagged value as stored in the blackboard and serialized in save data. */
export type TaggedValue =
  | { type: "num"; v: number }
  | { type: "str"; v: string }
  | { type: "bool"; v: boolean }
  | { type: "ternary"; v: TernaryValue }
  | { type: "list"; v: TaggedValue[] }
  | { type: "dict"; v: Record<string, TaggedValue> };

export type Listener = (key: string, value: TaggedValue) => void;

/** Wrap a raw JS value into a TaggedValue. */
export function tagValue(raw: unknown): TaggedValue {
  if (raw instanceof Ternary) return { type: "ternary", v: raw.value };
  if (typeof raw === "number") return { type: "num", v: raw };
  if (typeof raw === "string") return { type: "str", v: raw };
  if (typeof raw === "boolean") return { type: "bool", v: raw };
  if (Array.isArray(raw)) return { type: "list", v: raw.map(tagValue) };
  if (raw !== null && typeof raw === "object") {
    const out: Record<string, TaggedValue> = {};
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      out[k] = tagValue(v);
    }
    return { type: "dict", v: out };
  }
  // Default: treat as Unknown ternary
  return { type: "ternary", v: 0 };
}

/** Raw JS value extracted from a TaggedValue. */
export type UntaggedValue = number | string | boolean | Ternary | UntaggedValue[] | { [key: string]: UntaggedValue };

/** Extract the raw JS value from a TaggedValue. */
export function untagValue(tv: TaggedValue): UntaggedValue {
  switch (tv.type) {
    case "num": return tv.v;
    case "str": return tv.v;
    case "bool": return tv.v;
    case "ternary": return Ternary.of(tv.v);
    case "list": return tv.v.map(untagValue);
    case "dict": {
      const out: { [key: string]: UntaggedValue } = {};
      for (const [k, v] of Object.entries(tv.v)) out[k] = untagValue(v);
      return out;
    }
  }
}

/** The Unknown tagged value, returned on miss. */
const UNKNOWN_TAG: TaggedValue = { type: "ternary", v: 0 };

const MAX_SCOPE_DEPTH = 32;

export type VarType = "int" | "float" | "str" | "bool" | "Ternary" | "list" | "dict";

const TAG_TO_VARTYPE: Record<string, VarType[]> = {
  num: ["int", "float"],
  str: ["str"],
  bool: ["bool"],
  ternary: ["Ternary"],
  list: ["list"],
  dict: ["dict"],
};

export class Blackboard {
  private _data: Map<string, TaggedValue> = new Map();
  private _types: Map<string, VarType> = new Map();
  private _listeners: Map<string, Listener[]> = new Map();
  /** Global listeners notified on any write. */
  private _globalListeners: Listener[] = [];
  /** Scope stack for function argument bindings. */
  private _scopes: Array<Map<string, TaggedValue>> = [];

  /** Read a variable. Checks scope stack first, then global. Returns U-tagged on miss. */
  get(key: string): TaggedValue {
    for (let i = this._scopes.length - 1; i >= 0; i--) {
      const v = this._scopes[i].get(key);
      if (v !== undefined) return v;
    }
    return this._data.get(key) ?? UNKNOWN_TAG;
  }

  registerType(key: string, varType: VarType): void {
    this._types.set(key, varType);
  }

  getType(key: string): VarType | undefined {
    return this._types.get(key);
  }

  /** Write a variable to the global map and notify listeners. */
  set(key: string, value: TaggedValue): void {
    const declared = this._types.get(key);
    if (declared) {
      const compatible = TAG_TO_VARTYPE[value.type];
      if (compatible && !compatible.includes(declared)) {
        console.warn(`Blackboard: type mismatch for "${key}" — expected ${declared}, got ${value.type}`);
      }
    }

    const old = this._data.get(key);
    this._data.set(key, value);

    // Only fire if value changed
    if (!old || old.type !== value.type || old.v !== value.v) {
      const keyListeners = this._listeners.get(key);
      if (keyListeners) {
        for (const fn of keyListeners) fn(key, value);
      }
      for (const fn of this._globalListeners) fn(key, value);
    }
  }

  /** Push a scope overlay for function argument bindings. */
  pushScope(bindings: Record<string, TaggedValue>): void {
    if (this._scopes.length >= MAX_SCOPE_DEPTH) {
      throw new Error(`Blackboard: scope depth limit (${MAX_SCOPE_DEPTH}) exceeded — possible infinite recursion`);
    }
    const scope = new Map<string, TaggedValue>();
    for (const [k, v] of Object.entries(bindings)) {
      scope.set(k, v);
    }
    this._scopes.push(scope);
  }

  /** Pop the top scope overlay. */
  popScope(): void {
    this._scopes.pop();
  }

  get scopeDepth(): number {
    return this._scopes.length;
  }

  /** Check if a variable has been written. */
  has(key: string): boolean {
    for (let i = this._scopes.length - 1; i >= 0; i--) {
      if (this._scopes[i].has(key)) return true;
    }
    return this._data.has(key);
  }

  /** Register a listener for a specific variable. */
  listen(key: string, fn: Listener): void {
    const list = this._listeners.get(key);
    if (list) {
      list.push(fn);
    } else {
      this._listeners.set(key, [fn]);
    }
  }

  /** Register a listener for all writes. */
  listenAll(fn: Listener): void {
    this._globalListeners.push(fn);
  }

  /** Remove a previously registered global listener. */
  removeGlobalListener(fn: Listener): void {
    const idx = this._globalListeners.indexOf(fn);
    if (idx !== -1) this._globalListeners.splice(idx, 1);
  }

  /**
   * Write a value to a nested path inside a root variable.
   *
   * Root is deep-cloned before mutation so prior TaggedValue references
   * are not affected. The modified root is written back via set(), which
   * fires listeners keyed on the root variable name.
   *
   * Missing intermediate dict keys are created automatically.
   * Out-of-bounds list indices are silently ignored.
   */
  setPath(root: string, parts: (string | number)[], value: TaggedValue): void {
    if (parts.length === 0) {
      this.set(root, value);
      return;
    }

    const existing = this.get(root);
    // Deep-clone the root so we don't mutate existing references.
    // For non-dict/list roots we start with a fresh empty dict.
    const cloned: TaggedValue =
      existing.type === "dict" || existing.type === "list"
        ? structuredClone(existing)
        : { type: "dict", v: {} };

    let current: TaggedValue = cloned;
    for (let i = 0; i < parts.length - 1; i++) {
      const seg = parts[i];
      if (current.type === "dict") {
        const key = String(seg);
        // Auto-create missing intermediate keys as empty dicts
        if (!(key in current.v) || (current.v[key].type !== "dict" && current.v[key].type !== "list")) {
          current.v[key] = { type: "dict", v: {} };
        }
        current = current.v[key];
      } else if (current.type === "list" && typeof seg === "number") {
        if (seg < 0 || seg >= current.v.length) return;
        current = current.v[seg];
      } else {
        return; // Type mismatch — can't navigate further
      }
    }

    const lastSeg = parts[parts.length - 1];
    if (current.type === "dict") {
      current.v[String(lastSeg)] = value;
    } else if (current.type === "list" && typeof lastSeg === "number") {
      if (lastSeg >= 0 && lastSeg < current.v.length) {
        current.v[lastSeg] = value;
      } else {
        return; // Out-of-bounds
      }
    } else {
      return; // Can't set on this type
    }

    this.set(root, cloned);
  }

  /** Snapshot for serialization. */
  snapshot(): Record<string, TaggedValue> {
    const out: Record<string, TaggedValue> = {};
    for (const [k, v] of this._data) {
      out[k] = v;
    }
    return out;
  }

  /** Snapshot type metadata for serialization. */
  snapshotTypes(): Record<string, VarType> {
    const out: Record<string, VarType> = {};
    for (const [k, v] of this._types) {
      out[k] = v;
    }
    return out;
  }

  /** Clear all values and type metadata. Used when starting a fresh game. */
  clear(): void {
    this._data.clear();
    this._types.clear();
  }

  /** Restore from a snapshot. Does NOT fire listeners. */
  restore(data: Record<string, TaggedValue>): void {
    this._data.clear();
    for (const [k, v] of Object.entries(data)) {
      this._data.set(k, v);
    }
  }

  /** Restore type metadata from a snapshot. */
  restoreTypes(types: Record<string, VarType>): void {
    this._types.clear();
    for (const [k, v] of Object.entries(types)) {
      this._types.set(k, v);
    }
  }
}
